import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { propEq, sortBy, prop, reject } from 'ramda'
import {
  Box,
  Stack,
  TextLinkButton,
  Inline,
  Text,
  IconAdd,
  useDialogControllers
} from '@machobear/design-system'
import { select } from '@/store'
import { useUserLimits } from '@/hooks/use-blocks'

import UserPicker from '@/components/user-picker'
import LandlordCard from './landlord-card'
import { FormBox, FormBoxSkeleton, Counter } from '../../components'
import { Landlord } from '../../types'

const alphabetize = sortBy(prop('preferred_name'))

interface LandlordSectionProps {
  isLoading: boolean
  primaryLandlord: Landlord[]
  secondaryLandlords: Landlord[]
  setPrimaryLandlord: (landlord: Landlord[]) => void
  setSecondaryLandlords: (landlords: Landlord[]) => void
}

const LandlordSection = ({
  isLoading,
  primaryLandlord,
  secondaryLandlords,
  setPrimaryLandlord,
  setSecondaryLandlords
}: LandlordSectionProps) => {
  const { t } = useTranslation()

  const allLandlords = alphabetize(useSelector(select.user.companyUsers))

  const { data: userLimits } = useUserLimits()
  const primaryCount = primaryLandlord.length
  const secondaryCount = secondaryLandlords.length
  const totalCount = primaryCount + secondaryCount
  const maxCount = userLimits?.value || 1

  const { visible, showModal, closeModal } = useDialogControllers({
    primaryLandlordModal: false,
    secondaryLandlordModal: false
  })

  const handleDeleteSecondary = (user: Landlord) => {
    const updatedData = reject(propEq('id', user.id), secondaryLandlords)
    setSecondaryLandlords(updatedData)
  }

  const handleSetPrimary = (user: Landlord[]) => {
    const primaryCopy = user[0]
    primaryCopy.primary = true
    setPrimaryLandlord([primaryCopy])
  }

  const handleSetSecondary = (users: Landlord[]) => {
    setSecondaryLandlords(users)
  }

  return (
    <>
      <FormBox
        label={t('Landlord Management', 'Landlord Management')}
        icon={
          <Counter
            name={t('Landlord', 'Landlord')}
            count={totalCount}
            max={maxCount}
            isLoading={isLoading}
          />
        }
      >
        {isLoading ? (
          <FormBoxSkeleton />
        ) : (
          <Box role="list">
            <Stack space="medium">
              {primaryLandlord.map(landlord => (
                <LandlordCard
                  key={`landlord-${landlord.id}`}
                  landlord={landlord}
                  isPrimary
                  handleRemove={showModal('primaryLandlordModal')}
                />
              ))}
              {secondaryLandlords.map(landlord => (
                <LandlordCard
                  key={`landlord-${landlord.id}`}
                  landlord={landlord}
                  handleRemove={() => handleDeleteSecondary(landlord)}
                />
              ))}
              <Text baseline={false}>
                <TextLinkButton onClick={showModal('secondaryLandlordModal')}>
                  <Inline space="xxxsmall">
                    <IconAdd />
                    <Box>{t('Add landlord', 'Add landlord')}</Box>
                  </Inline>
                </TextLinkButton>
              </Text>
            </Stack>
          </Box>
        )}
      </FormBox>
      <UserPicker
        visible={visible.primaryLandlordModal}
        onClose={closeModal('primaryLandlordModal')}
        maxSelect={1}
        allUsers={allLandlords as never[]}
        activeUsers={[]}
        disabledUsers={secondaryLandlords as never[]}
        setActiveUsers={handleSetPrimary}
        title={t('l.listing.edit.remove_landlord.title', 'Remove landlord')}
        subtitle={t(
          'l.listing.edit.remove_landlord.subtitle',
          'Please select the new primary landlord for this listing:'
        )}
        showPrimaryLandlordAlert
        alertTrackingId="app_upgrade_landlord_management_anchortext"
      />
      <UserPicker
        visible={visible.secondaryLandlordModal}
        onClose={closeModal('secondaryLandlordModal')}
        maxSelect={maxCount - primaryCount}
        allUsers={allLandlords as never[]}
        activeUsers={secondaryLandlords as never[]}
        disabledUsers={primaryLandlord as never[]}
        setActiveUsers={handleSetSecondary}
        showLandlordAlerts
        alertTrackingId="app_upgrade_landlord_management_anchortext"
      />
    </>
  )
}

export default LandlordSection
